import { Button } from '@eventbrite/marmalade';
import { InputField } from '@eventbrite/legacy-eds.input-field';
import { useIntl } from 'react-intl';
import React, { useEffect, useMemo } from 'react';
import styles from './ReportEventFormFields.module.scss';
import { reportEvent } from '../../api';
import {
    useErrorDisplayContext,
    usePaginationDisplayContext,
    useRadioContext,
} from '../../context';
import { useForm } from '../../hooks';
import { Pages } from '../../types';
import { computeRegulationLabel } from '../../util';

export const ReportEventFormFields = (props: {
    eventId: string;
    eventName: string;
    eventUrl: string;
    country: string;
    userCountry: string;
}) => {
    const intl = useIntl();
    const { eventId, eventName, eventUrl, country, userCountry } = props;
    const { fields, helpers } = useForm();
    const { selection } = useRadioContext();
    const { setError } = useErrorDisplayContext();
    const { setPage } = usePaginationDisplayContext();
    const emailRequired = selection === 'reason_unauthorized_event';
    const descriptionRequired = [
        'reason_unauthorized_event',
        'reason_harmful',
        'reason_canceled_event',
    ].includes(selection || '');

    const reportHelperText: { [key: string]: string } = useMemo(() => ({
        reason_unauthorized_event: intl.formatMessage({
            id: 'This event is a copy of another event, is reselling tickets, or the tickets I purchased were not valid for entry. Include the URL for the authorized event and your relationship to the event.',
            defaultMessage: 'This event is a copy of another event, is reselling tickets, or the tickets I purchased were not valid for entry. Include the URL for the authorized event and your relationship to the event.',
        }),
        reason_harmful: intl.formatMessage({
            id: 'Ex: bullying, harmful misinformation',
            defaultMessage: 'Ex: bullying, harmful misinformation',
        }),
        reason_offensive_content: intl.formatMessage({
            id: 'Ex: drugs, gambling',
            defaultMessage: 'Ex: drugs, gambling',
        }),
        reason_explicit: intl.formatMessage({
            id: 'Ex: graphic nudity, pornography',
            defaultMessage: 'Ex: graphic nudity, pornography',
        }),
        reason_hateful: intl.formatMessage({
            id: 'Ex: threats or intimidation of a protected group',
            defaultMessage: 'Ex: threats or intimidation of a protected group',
        }),
        reason_extremism: intl.formatMessage({
            id: 'Ex: graphic violence, dangerous organizations',
            defaultMessage: 'Ex: graphic violence, dangerous organizations',
        }),
        reason_canceled_event: intl.formatMessage({
            id: 'The event was canceled, postponed, rescheduled, or did not deliver the advertised experience. Include details about how you were informed of any changes and how the event was different than what was advertised.',
            defaultMessage: 'The event was canceled, postponed, rescheduled, or did not deliver the advertised experience. Include details about how you were informed of any changes and how the event was different than what was advertised.',
        }),
        report_default: intl.formatMessage({
            id: 'Additional details are required for all reported issues.',
            defaultMessage: 'Additional details are required for all reported issues.',
        }),
        reason_spam_behavior: intl.formatMessage({
            id: "The listing is not advertising an event (ex: it promotes a product or service, or it's unclear what is being advertised), is listed repeatedly and is interfering with search results, or it includes deceptive or exaggerated claims about the success of using the advertised services.",
            defaultMessage: "The listing is not advertising an event (ex: it promotes a product or service, or it's unclear what is being advertised), is listed repeatedly and is interfering with search results, or it includes deceptive or exaggerated claims about the success of using the advertised services.",
        }),
        reason_payment_outside: intl.formatMessage({
            id: 'This event asks for payment at the door or an alternate payment platform such as PayPal, CashApp, or Venmo.',
            defaultMessage: 'This event asks for payment at the door or an alternate payment platform such as PayPal, CashApp, or Venmo.',
        }),
    }), [intl]);

    useEffect(() => {
        helpers.setters.setRequired({
            email: emailRequired,
            description: descriptionRequired,
        });

        if (!fields.required.email)
            helpers.setters.setEmail({ ...fields.email, error: '' });
        if (!fields.required.description)
            helpers.setters.setDescription({
                ...fields.description,
                error: '',
            });
    }, []);

    const handleSubmit = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        let emailError = false;
        let descriptionError = false;

        if (fields.required.email) emailError = helpers.validateField('email');
        if (fields.required.description)
            descriptionError = helpers.validateField('description');

        if (!emailError && !descriptionError) {
            const regulationLabel = computeRegulationLabel(
                country,
                userCountry,
            );
            reportEvent({
                userId: fields.userId,
                email: fields.email.value || '',
                eventId,
                reportMessage: fields.description.value || '',
                reportReason: selection || 'reason_other',
                eventUrl,
                eventName,
                regulation: regulationLabel,
            })
                .then((response: Response) => response.json())
                .then((status) => {
                    if (status.message === 'success') {
                        setPage?.(Pages.success);
                    } else {
                        throw new Error('Error submitting report.');
                    }
                })
                .catch(() => {
                    setError?.(true);
                });
        }
    };

    return (
        <>
            <section className={styles.emailSection}>
                <InputField
                    id="report-email"
                    data-testid="report-email-input"
                    label={intl.formatMessage({
                        id: 'Your Email',
                        defaultMessage: 'Your Email',
                    })}
                    placeholder={intl.formatMessage({
                        id: 'name@example.com',
                        defaultMessage: 'name@example.com',
                    })}
                    type="email"
                    onChange={(text: string) =>
                        helpers.setters.setEmail({
                            ...fields.email,
                            value: text,
                        })
                    }
                    value={fields.email.value}
                    hasError={!!fields.email.error}
                    required={fields.required.email}
                    style="static"
                />
                {fields.email.error && (
                    <p className={styles.errorText}>
                        {fields.email.error}
                    </p>
                )}
            </section>
            <section className={styles.descriptionSection}>
                <InputField
                    id="report-description"
                    data-testid="report-description-input"
                    label={intl.formatMessage({
                        id: 'Description',
                        defaultMessage: 'Description',
                    })}
                    placeholder={
                        reportHelperText[selection || 'report_default']
                    }
                    onChange={(text: string) =>
                        helpers.setters.setDescription({
                            ...fields.description,
                            value: text,
                        })
                    }
                    value={fields.description.value}
                    hasError={!!fields.description.error}
                    required={fields.required.description}
                    isMultiline={true}
                    style="static"
                    type="text"
                />
                {fields.description.error && (
                    <p className={styles.errorText}>
                        {fields.description.error}
                    </p>
                )}
            </section>
            <Button
                data-testid="report-event-submit"
                onClick={handleSubmit}
                variant="primary"
                className={styles.submitButton}
            >
                {intl.formatMessage({
                    id: 'Submit Report',
                    defaultMessage: 'Submit Report',
                })}
            </Button>
            <Button
                data-testid="report-event-back"
                onClick={() => {
                    setPage?.(Pages.intro);
                }}
                variant="secondary"
                className={styles.backButton}
            >
                {intl.formatMessage({
                    id: 'Go back',
                    defaultMessage: 'Go back',
                })}
            </Button>
        </>
    );
};
